import { useCallback, useMemo } from 'react';

import {
  YAxis as RechartsYAxis,
  YAxisProps as RechartsYAxisProps,
} from 'recharts';

import { buildTickFormatter } from './_utils';
import { YAxisProps, YAxisTickProps } from './types';
import { YAxisTick } from './YAxisTick';
import { AxisTickConfig } from '../../_types';
import { tokens } from '../../../../theme/generated/tokens';

const defaultYAxisProps = {
  interval: 'preserveStart',
  minTickGap: 8,
  tickMargin: 4,
  allowDecimals: false,
  allowDuplicatedCategory: false,
  axisLine: false,
  tickLine: false,
} satisfies Partial<YAxisProps<unknown>>;

const defaultTickConfig: AxisTickConfig = {
  backgroundColor: tokens.theme.bg.structural.surface,
  color: tokens.theme.fg.structural.secondary,
  paddingX: 2,
  paddingY: 2,
  fontSize: 10,
};

export const YAxis = <T, V = string | number>({
  tick,
  dataKey,
  unitFormatter,
  tickFormatter,
  ...rest
}: YAxisProps<T, V>) => {
  const finalProps = { ...defaultYAxisProps, ...rest };

  const tickConfig = useMemo(() => {
    return typeof tick === 'object'
      ? { ...defaultTickConfig, ...tick }
      : defaultTickConfig;
  }, [tick]);

  const renderTick = useCallback(
    (props: YAxisTickProps<V>) => (
      <YAxisTick
        {...props}
        tickConfig={tickConfig}
        tickFormatter={buildTickFormatter({
          unitFormatter,
          tickFormatter,
          index: props.index,
          visibleTicksCount: props.visibleTicksCount,
        })}
      />
    ),
    [tickConfig, unitFormatter, tickFormatter]
  );

  return (
    <RechartsYAxis
      className="y-axis-container"
      dataKey={dataKey as RechartsYAxisProps<T, V>['dataKey']}
      {...finalProps}
      tick={typeof tick === 'function' ? tick : renderTick}
      mirror={true}
    />
  );
};
